import { css } from '@emotion/react';
import {
  faArrowLeft, faPrint,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import {
  ButtonComponent, ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import { CONFIG } from '~/_shared/constants/config';
import { useTranslation } from '~/_shared/utils/hooks';
import { ExportReportContentContainer } from '~/exportReport/exportReportContent.container';
import {
  getDevPdfReportPath, saveDevExportReportSnapshot,
} from '~/exportReport/printPage/devExportReportPrintPage.helpers';
import { useExportReportData } from '~/exportReport/useExportReportData';
import { useExportReportParams } from '~/exportReport/useExportReportParams';
import type { ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { PurchaseDataPackageType } from '~/purchaseDataPackage/modal/purchaseDataPackageModal.component';
import { createReportCreateRequest } from '~/store/frontendState/createReport/createReport.actionCreators';
import { exportReportDisable } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.actionCreators';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';

export type MapExportReportPreviewModalModalProps = ModalProps<{
  onBackToReportsClick: () => void;
}>;

const previewWrapperStyle = css({
  minWidth: 1000,
});

const reportWrapperStyle = css({
  padding: 20,
  maxWidth: 1000,
  margin: '0 auto',
});

const alertWrapperStyle = css({
  padding: 20,
});

const devPrintButtonStyle = css({
  marginLeft: 8,
});

const modalContentStyle = (isLoading: boolean) => css({
  padding: 0,
  position: 'relative',
  maxHeight: isLoading ? '100%' : undefined,
  overflow: isLoading ? 'hidden' : undefined,
});

export const MapExportReportPreviewModalContainer: FC<MapExportReportPreviewModalModalProps> = ({ onClose, isOpen, onBackToReportsClick }) => {
  const { isActive: isExportActive, settings, params } = useToolsStateExportReportSelector();
  const { sample: sampleReport } = settings;
  const mapId = useMapIdSelector();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { openModal: openPurchaseDataPackageModal } = useModal('PurchaseDataPackage');
  const { tableColumns } = useExportReportParams();

  const { isLoading, getDemographicData, getDemographicLabel, isError, isReportEmpty } = useExportReportData({
    tableColumns,
  });

  const handleClose = useCallback(() => {
    dispatch(exportReportDisable());
    onClose();
  }, [dispatch, onClose]);

  const handlePrint = useCallback(() => {
    if (!mapId) {
      return;
    }

    dispatch(createReportCreateRequest(mapId));
    onClose();
  }, [mapId, dispatch, onClose]);

  const handleBackToReportsClick = useCallback(() => {
    onBackToReportsClick();
    onClose();
  }, [onBackToReportsClick, onClose]);

  const handleOpenDevPrintPageClick = useCallback(() => {
    if (!mapId || !params) {
      return;
    }

    saveDevExportReportSnapshot(mapId, { params, settings });
    window.open(getDevPdfReportPath(mapId), '_blank', 'noopener');
  }, [mapId, params, settings]);

  const handlePurchaseDataPackageClick = useCallback(() => {
    openPurchaseDataPackageModal({
      type: PurchaseDataPackageType.ExportReport,
    });
  }, [openPurchaseDataPackageModal]);

  if (!isExportActive) {
    return null;
  }

  const isSampleReport = !!sampleReport;

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={handleClose}
      caption={t('exportReport.previewModal.caption')}
      leftFooterContent={(
        <>
          <ButtonComponent
            text={t('exportReport.sampleReport.backToReports')}
            buttonStyle={ButtonStyle.Secondary}
            prefixIcon={faArrowLeft}
            onClick={handleBackToReportsClick}
          />

          {CONFIG.MODE !== 'production' && CONFIG.ENABLE_DEV_PDF_REPORT_PRINT_PAGE && (
            <ButtonComponent
              css={devPrintButtonStyle}
              text="Open Print Page (dev)"
              buttonStyle={ButtonStyle.Secondary}
              prefixIcon={faPrint}
              onClick={handleOpenDevPrintPageClick}
            />
          )}
        </>
      )}
      confirmButton={isSampleReport ? (
        <ButtonComponent
          text={t('exportReport.sampleReport.purchaseDataPackage')}
          buttonStyle={ButtonStyle.Success}
          onClick={handlePurchaseDataPackageClick}
        />
      ) : (
        <ButtonComponent
          isDisabled={isLoading || isError || isReportEmpty}
          prefixIcon={faPrint}
          text={t('Print Report')}
          onClick={handlePrint}
        />
      )}
      contentStyle={modalContentStyle(isLoading)}
      maxWidth={1100}
    >
      <ScrollBarComponent type={ScrollbarType.Horizontal}>
        <div css={previewWrapperStyle}>
          {(isError || isReportEmpty) && (
            <div css={alertWrapperStyle}>
              <AlertComponent type="danger">
                {isError ? t('Error Exporting a Report') : t('No Data Available for This Area')}
              </AlertComponent>
            </div>
          )}

          {!isError && !isReportEmpty && (
            <div css={reportWrapperStyle}>
              <ExportReportContentContainer
                tableColumns={tableColumns}
                getDemographicData={getDemographicData}
                getDemographicLabel={getDemographicLabel}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>
      </ScrollBarComponent>

      {isLoading && (
        <OverlayLoaderComponent />
      )}
    </ModalComponent>
  );
};
